import React, { useEffect, useState } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "../../components/ui/Button";
import { FileUpload } from "primereact/fileupload";
import { Toast } from "primereact/toast";
import { useRef } from "react";
import { SequencesPage } from "./sequence";
import { useDispatch } from "react-redux";
import { getUserByTenantAndUserId } from "../../service/user/userService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFloppyDisk, faImage } from "@fortawesome/free-solid-svg-icons";
import {
  createTenantDetails,
  getTenantDetailByTenantId,
  uploadLogo, // Import the new function
} from "../../service/tenantDetails/TenantDetailsService";
import { getAllCountries } from "../../service/countries/CountryServices";
import { InputSwitch } from "primereact/inputswitch";
import { UpdateUserPermission } from "../../dtos/role/updateUserPermission";
import {
  getPermissionByUserAndPermissionName,
  updateUserPermission,
} from "../../service/permission/permissionService";
import { showToast } from "../../store/slices/toastSlice";
import { setProfilePic } from "../../store/slices/profilePicSlice";
import { TenantPaymentSettingsPage } from "../TenantPaymentSettings/TenantPaymentSettingsPage";
import { getValidIntegerOrNull } from "../../utils/storageUtils";

// Add this interface for the upload logo DTO
interface UploadLogoDto {
  logo: string;
}

interface BusinessProfile {
  tenancyName: string;
  logo: string;
  contactPerson: string;
  name: string;
  surname: string;
  isActive: boolean;
  email: string;
  phone_number: string;
  // Address fields
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  country_id: string;
  website_url: string;
}

interface InvoiceSettings {
  numberSequence: string;
  nextNumber: number;
  defaultDueDays: number;
  defaultNotes: string;
  defaultTerms: string;
  emailTemplate: string;
  defaultTemplateId: string;
}

interface CreateTenantDetailDto {
  tenant_id: number;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country_id?: string | null;
  logo?: string | null;
  website_url?: string | null;
  createdBy?: number;
}

export const Settings: React.FC = () => {
  const dispatch = useDispatch();
  const options = ["Active", "Inactive"];
  const [value, setValue] = useState(options[0]);
  const [businessProfile, setBusinessProfile] = useState<BusinessProfile>({
    tenancyName: "My Business",
    logo: "",
    contactPerson: "John Doe",
    email: "contact@mybusiness.com",
    phone_number: "+1-555-123-4567",
    name: "john",
    surname: "Doe",
    isActive: true,
    website_url: "",
    // Address fields
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    country_id: "",
  });

  const [invoiceSettings, setInvoiceSettings] = useState<InvoiceSettings>({
    numberSequence: "INV-{YEAR}-{SEQUENTIAL}",
    nextNumber: 1,
    defaultDueDays: 30,
    defaultNotes: "Thank you for your business!",
    defaultTerms: "Payment is due within the specified payment terms.",
    emailTemplate:
      "Dear {CLIENT_NAME},\n\nPlease find attached your invoice {INVOICE_NUMBER}.\n\nPayment is due by {DUE_DATE}.\n\nThank you for your business.",
    defaultTemplateId: "template1",
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [uploadLoading, setUploadLoading] = useState<boolean>(false);
  const toast = useRef<Toast>(null);
  const [countries, setCountries] = useState<
    { label: string; value: number }[]
  >([]);
  const [notificationPermission, setNotificationPermission] =
    useState<boolean>(false);
  const fileUploadRef = useRef<any>(null);

  const handleLogoUpload = async (event: any) => {
    const file = event.files?.[0];
    if (!file) return; // No file selected, do nothing

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      dispatch(
        showToast({
          severity: "error",
          summary: "Invalid format",
          detail: "Only JPG or PNG allowed.",
        })
      );
      fileUploadRef.current.clear();
      return;
    }

    // 2) Check dimensions
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    const dimsOk: boolean = await new Promise((resolve) => {
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(img.width <= 250 && img.height <= 250);
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(false);
      };
      img.src = objectUrl;
    });

    if (!dimsOk) {
      dispatch(
        showToast({
          severity: "error",
          summary: "Invalid size",
          detail: "Logo dimensions must be at most 250×250 px.",
        })
      );
      fileUploadRef.current.clear();
      return;
    }

    setUploadLoading(true);
    try {
      const userIdString = localStorage.getItem("userId");
      if (!userIdString) throw new Error("User ID not found");

      const base64data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => {
          reader.result
            ? resolve(reader.result as string)
            : reject("Failed to read file");
        };
        reader.onerror = reject;
      });

      const base64Content = base64data.split(",")[1];

      const logoData: UploadLogoDto = { logo: base64Content };

      const response = await uploadLogo(dispatch, logoData);

      if (response) {
        setBusinessProfile((prev) => ({
          ...prev,
          logo: base64data,
        }));

        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Logo uploaded successfully",
          life: 3000,
        });

        fileUploadRef.current?.clear();
      } else {
        throw new Error("Failed to upload logo");
      }
    } catch (error: any) {
      console.error("Error uploading logo:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "Failed to upload logo",
        life: 5000,
      });
    } finally {
      setUploadLoading(false);
    }
  };

  const saveBusinessProfile = async () => {
    setLoading(true);
    try {
      // Get tenant and user info from localStorage
      const tenantIdString = localStorage.getItem("tenantId");
      const userIdString = localStorage.getItem("userId");

      if (!tenantIdString || !userIdString) {
        throw new Error("Required tenant or user information not found");
      }

      const tenantId = JSON.parse(tenantIdString);
      const userId = JSON.parse(userIdString);

      // Prepare data for API call
      const tenantDetailData: CreateTenantDetailDto = {
        tenant_id: tenantId,
        address_line1: businessProfile.address_line1 || null,
        address_line2: businessProfile.address_line2 || null,
        city: businessProfile.city || null,
        state: businessProfile.state || null,
        postal_code: businessProfile.postal_code || null,
        country_id: businessProfile.country_id || null,
        logo: businessProfile.logo || null,
        website_url: businessProfile.website_url,
        createdBy: userId,
      };

      // Call the API
      const response = await createTenantDetails(dispatch, tenantDetailData);

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Business profile saved successfully",
          life: 3000,
        });
        dispatch(setProfilePic(businessProfile.logo));
      } else {
        throw new Error("Failed to save business profile");
      }
    } catch (error: any) {
      console.error("Error saving business profile:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "Failed to save business profile",
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessProfileChange = (field: string, value: any) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setBusinessProfile((prev) => ({
        ...prev,
        [parent]: {
          // ...(prev[parent as keyof BusinessProfile] as object),
          [child]: value,
        },
      }));
    } else {
      setBusinessProfile((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const userInfoByTenantId = async () => {
    // Get userId - mandatory (throw error if not found)
    const userIdString = localStorage.getItem("userId");
    if (!userIdString) {
      throw new Error("User ID not found in localStorage");
    }
    const userId = JSON.parse(userIdString);

    // Get tenantId - optional (can be null)
    const tenantIdString = localStorage.getItem("tenantId");
    const tenantId = tenantIdString ? JSON.parse(tenantIdString) : null;

    const input = {
      userId,
      tenantId, // This can be null
    };

    const userInfo = await getUserByTenantAndUserId(dispatch, input);
    if (userInfo?.user?.isActive) {
      setValue(options[0]);
    } else {
      setValue(options[1]);
    }
    const tenancyName = userInfo?.tenant?.tenancyName;
    setBusinessProfile((prev) => ({
      ...prev,
      tenancyName:
        tenancyName === ""
          ? "Default"
          : tenancyName === null
            ? "host"
            : tenancyName === undefined
              ? "Not available"
              : tenancyName,
      logo: "",
      contactPerson: userInfo?.user.userName,
      name: userInfo?.user.name,
      surname: userInfo?.user.surname,
      isActive: userInfo?.user?.isActive,
      email: userInfo?.user?.emailAddress,
      phone: userInfo?.user?.phoneNumber || "Not available", // Empty string if null
    }));
    return userInfo;
  };

  const fetchTenantDetails = async () => {
    try {
      const tenantIdString = localStorage.getItem("tenantId");
      if (!tenantIdString) {
        console.log("No tenant ID found, skipping tenant details fetch");
        return;
      }

      const tenantId = JSON.parse(tenantIdString);
      const response = await getTenantDetailByTenantId(dispatch, tenantId);

      if (response) {
        const tenantDetail = response;

        // Update business profile with tenant details
        setBusinessProfile((prev) => ({
          ...prev,
          address_line1: tenantDetail.address_line1 || "",
          address_line2: tenantDetail.address_line2 || "",
          city: tenantDetail.city || "",
          state: tenantDetail.state || "",
          postal_code: tenantDetail.postal_code || "",
          country_id: tenantDetail.country_id || "",
          logo: tenantDetail.logo || "",
          website_url: response.website_url,
        }));
        dispatch(setProfilePic(tenantDetail.logo));
      }
    } catch (error) {
      console.error("Error fetching tenant details:", error);
      // Don't show error toast for missing tenant details as it's optional
    }
  };

  const handleInvoiceSettingsChange = (field: string, value: any) => {
    setInvoiceSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateNotification = async () => {
    setNotificationPermission(!notificationPermission);
    const props: UpdateUserPermission = {
      userId: parseInt(localStorage.getItem("userId") || "0"),
      tenantId: getValidIntegerOrNull("tenantId"),
      permissionName: "Pages.Notifications",
    };
    await updateUserPermission(dispatch, props);
    fetchNotification();
    dispatch(
      showToast({
        severity: "success",
        summary: "Success",
        detail: "Notification settings updated successfully",
        life: 3000,
      })
    );
  };

  const fetchCountries = async () => {
    const countriesResponse = await getAllCountries(dispatch, {
      Page: 1,
      Limit: 1000,
    });
    if (countriesResponse) {
      setCountries(
        countriesResponse.List.map((country: any) => ({
          label: country.country_name,
          value: country.country_id,
        }))
      );
    }
  };

  const fetchNotification = async () => {
    const response = await getPermissionByUserAndPermissionName(dispatch, {
      userId: parseInt(localStorage.getItem("userId") || "0"),
      permissionName: "Pages.Notifications",
    });
    if (response) {
      setNotificationPermission(response);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      await userInfoByTenantId();
      await fetchCountries();
      await fetchTenantDetails(); // Fetch existing tenant details after user info
    };

    initializeData();
    fetchNotification();
  }, []);

  return (
    <div>
      <Toast ref={toast} />

      <div className="flex justify-between items-center mb-0">
        <h1 className="text-xl font-bold surface-900 mb-4">Settings</h1>
      </div>

      <TabView className="csTab p-0">
        <TabPanel className="p-0" header="Business Profile">
          <div className="mb-6">
            <div className="p-fluid grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <h2 className="text-xl font-semibold mt-0 mb-3 text-gray-800">
                  Business Information
                </h2>
              </div>

              <div className="field">
                <label
                  htmlFor="tenancyName"
                  className="block text-sm font-medium text-gray-800 mb-1"
                >
                  Tenancy Name
                </label>
                <InputText
                  disabled
                  id="tenancyName"
                  value={businessProfile.tenancyName}
                  onChange={(e) =>
                    handleBusinessProfileChange("tenancyName", e.target.value)
                  }
                />
              </div>
              <div className="field">
                <label
                  htmlFor="contactPerson"
                  className="block text-sm font-medium text-gray-800 mb-1"
                >
                  Contact Person
                </label>
                <InputText
                  disabled
                  id="contactPerson"
                  value={businessProfile.contactPerson}
                  onChange={(e) =>
                    handleBusinessProfileChange("contactPerson", e.target.value)
                  }
                />
              </div>
              <div className="field">
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-800 mb-1"
                >
                  Name
                </label>
                <InputText
                  disabled
                  id="name"
                  value={businessProfile.name}
                  onChange={(e) =>
                    handleBusinessProfileChange("name", e.target.value)
                  }
                />
              </div>
              <div className="field">
                <label
                  htmlFor="surname"
                  className="block text-sm font-medium text-gray-800 mb-1"
                >
                  Surname
                </label>
                <InputText
                  disabled
                  id="surname"
                  value={businessProfile.surname}
                  onChange={(e) =>
                    handleBusinessProfileChange("surname", e.target.value)
                  }
                />
              </div>

              <div className="field">
                <label
                  htmlFor="contactPerson"
                  className="block text-sm font-medium text-gray-800 mb-1"
                >
                  User Name
                </label>
                <InputText
                  disabled
                  id="contactPerson"
                  value={businessProfile.contactPerson}
                  onChange={(e) =>
                    handleBusinessProfileChange("contactPerson", e.target.value)
                  }
                />
              </div>

              <div className="field">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-800 mb-1"
                >
                  Email
                </label>
                <InputText
                  disabled
                  id="email"
                  value={businessProfile.email}
                  onChange={(e) =>
                    handleBusinessProfileChange("email", e.target.value)
                  }
                />
              </div>

              <div className="field">
                <label
                  htmlFor="phone_number"
                  className="block text-sm font-medium text-gray-800 mb-1"
                >
                  Phone
                </label>
                <InputText
                  id="phone_number"
                  value={businessProfile.phone_number}
                  onChange={(e) =>
                    handleBusinessProfileChange("phone_number", e.target.value)
                  }
                />
              </div>

              {/* website url */}
              <div className="field">
                <label
                  htmlFor="website_url"
                  className="block text-sm font-medium text-gray-800 mb-1"
                >
                  Website Url
                </label>
                <InputText
                  id="website_url"
                  value={businessProfile.website_url}
                  onChange={(e) =>
                    handleBusinessProfileChange("website_url", e.target.value)
                  }
                />
              </div>

              {/* logo  */}
              <div className="field">
                <label
                  htmlFor="logo"
                  className="block text-sm font-medium text-gray-800 mb-1"
                >
                  Logo
                </label>
                {businessProfile.logo && (
                  <div className="mb-3 p-2 border rounded-md">
                    <img
                      src={businessProfile.logo}
                      alt="Business Logo"
                      className="max-h-24 max-w-full"
                    />
                  </div>
                )}
                <FileUpload
                  ref={fileUploadRef}
                  mode="basic"
                  name="logo"
                  accept=".jpg,.jpeg,.png"
                  maxFileSize={1000000}
                  chooseLabel={uploadLoading ? "Uploading..." : "Choose Logo"}
                  disabled={uploadLoading}
                  auto={false}
                  onSelect={handleLogoUpload} // 👈 use onSelect instead
                  className="w-full"
                />

                <small className="text-gray-500 mt-1 block">
                  Upload your business logo (JPG/PNG, exactly 250×250px, max
                  1 MB).
                </small>
              </div>

              {/* Address Section */}
              <div className="md:col-span-2">
                <h3 className="text-lg font-semibold mb-3 text-gray-800 mt-4">
                  Address Information
                </h3>
              </div>

              <div className="field">
                <label
                  htmlFor="address_line1"
                  className="block text-sm font-medium text-gray-800 mb-1"
                >
                  Address Line 1
                </label>
                <InputText
                  id="address_line1"
                  value={businessProfile.address_line1}
                  onChange={(e) =>
                    handleBusinessProfileChange("address_line1", e.target.value)
                  }
                  placeholder="Street address, P.O. box"
                />
              </div>

              <div className="field">
                <label
                  htmlFor="address_line2"
                  className="block text-sm font-medium text-gray-800 mb-1"
                >
                  Address Line 2
                </label>
                <InputText
                  id="address_line2"
                  value={businessProfile.address_line2}
                  onChange={(e) =>
                    handleBusinessProfileChange("address_line2", e.target.value)
                  }
                  placeholder="Apartment, suite, unit, building, floor, etc."
                />
              </div>

              <div className="field">
                <label
                  htmlFor="city"
                  className="block text-sm font-medium text-gray-800 mb-1"
                >
                  City
                </label>
                <InputText
                  id="city"
                  value={businessProfile.city}
                  onChange={(e) =>
                    handleBusinessProfileChange("city", e.target.value)
                  }
                />
              </div>

              <div className="field">
                <label
                  htmlFor="state"
                  className="block text-sm font-medium text-gray-800 mb-1"
                >
                  State/Province
                </label>
                <InputText
                  id="state"
                  value={businessProfile.state}
                  onChange={(e) =>
                    handleBusinessProfileChange("state", e.target.value)
                  }
                />
              </div>

              <div className="field">
                <label
                  htmlFor="postal_code"
                  className="block text-sm font-medium text-gray-800 mb-1"
                >
                  Postal Code
                </label>
                <InputText
                  id="postal_code"
                  value={businessProfile.postal_code}
                  onChange={(e) =>
                    handleBusinessProfileChange("postal_code", e.target.value)
                  }
                />
              </div>

              <div className="field">
                <label
                  htmlFor="country_id"
                  className="block text-sm font-medium text-gray-800 mb-1"
                >
                  Country
                </label>
                <Dropdown
                  id="country_id"
                  value={businessProfile.country_id}
                  options={countries}
                  onChange={(e) =>
                    handleBusinessProfileChange("country_id", e.value)
                  }
                  placeholder="Select Country"
                  className="w-full"
                />
              </div>

              <div className="md:col-span-2 flex justify-end mt-4">
                <Button isLoading={loading} onClick={saveBusinessProfile}>
                  <FontAwesomeIcon icon={faFloppyDisk} />
                  {"  "}
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </TabPanel>

        <TabPanel header="Invoice Settings">
          <div className="mb-6">
            <SequencesPage />
          </div>
        </TabPanel>

        <TabPanel header="Invoice Payment Settings">
          <div className="mb-6">
            <TenantPaymentSettingsPage />
          </div>
        </TabPanel>

        <TabPanel header="Notifications">
          <div className="mb-6">
            <div className="p-0">
              <h2 className="text-xl font-semibold text-gray-800 mb-3 mt-0">
                Notification Settings
              </h2>
              <div className="flex items-center justify-between border-b border-gray-200 last:border-b-0 py-3">
                <div className="flex-1">
                  <h4 className="text-base font-medium text-gray-800 my-0 mb-0 mt-0">
                    {notificationPermission ? "Disable" : "Enable"} Notification
                    Settings
                  </h4>
                  {/* <p className="text-sm text-gray-600">{description}</p> */}
                </div>
                <div className="ml-4">
                  <InputSwitch
                    checked={notificationPermission}
                    onChange={(e) => updateNotification()}
                    className="scale-90"
                  />
                </div>
              </div>
            </div>
          </div>
        </TabPanel>
      </TabView>
    </div>
  );
};
